import { IllustrationBG } from "@src/common/IllustrationBG/IllustrationBG";
import { PageLayout } from "@src/common/PageLayout";
import { NavButtonsContainer } from "@src/common/styled-components";
import { UpdateAgeModal } from "@src/common/UpdateAgeModal";
import { APP_ROUTES, LAYOUT_CONSTANTS, MEDIA_QUERIES } from "@src/constants";
import { useComponentNameContext } from "@src/context";
import { type FormDetails, useApplicationContext } from "@src/context/ApplicationContext";
import { useCanvasContext } from "@src/context/CanvasContext";
import { FormProvider, useFormContext } from "@src/context/FormContext";
import { useExclusiveAction } from "@src/hooks/useExclusiveAction";
import { useNavigateWithUTM } from "@src/hooks/useNavigateWithUTM";
import { useResolvedPageLabels } from "@src/hooks/useResolvedPageLabels";
import { buildOutboundPayload, CANVAS_OUTBOUND_EVENT_NAME } from "@src/services/canvas/outbound";
import ClientLogsService from "@src/services/client-logs.service";
import { type AgeModalScenario, evaluateAgeModalScenario } from "@src/utils/age-modal-guard";
import { useMediaQuery } from "@uhg-abyss/web/hooks/useMediaQuery";
import { useParams } from "@uhg-abyss/web/tools/reactRouterTools";
import { styled } from "@uhg-abyss/web/tools/styled";
import { Button } from "@uhg-abyss/web/ui/Button";
import type { ValidIllustrationBrandName } from "@uhg-abyss/web/ui/IllustrationBrand";
import { Router } from "@uhg-abyss/web/ui/Router";
import { StepTracker } from "@uhg-abyss/web/ui/StepTracker";
import { ToastProvider } from "@uhg-abyss/web/ui/Toast";
import { Fragment, useEffect, useState } from "react";
import { markAgeRangeResetConfirmation } from "../GetStarted/ageRangeResetConfirmationSignal";
import { CoverCareStep } from "./EligibilityStep/CoverCareStep/CoverCareStep";
import { PolicyHolderDetailsStep } from "./EligibilityStep/PolicyHolderDetailsStep";
import { FormFooter } from "./FormFooter";
import {
  getBackNavigationPath,
  getFormNameForSubmit,
  getNavigationPath,
  getSelfPayRedirectPath,
  getSlugFromStepAndSubstep,
  getStepAndSubstepFromSlug,
  getStepTrackerConfig,
  patientInfoRoutes,
} from "./patientInformationNavigation";
import { ConfirmApptDetails } from "./ReviewDetailsStep";
import { YourInfoStep } from "./YourInfoStep";

export type { PatientInfoSlug } from "./patientInformationNavigation";
export {
  getBackNavigationPath,
  getFormNameForSubmit,
  getNavigationPath,
  getSelfPayRedirectPath,
  getSlugFromStepAndSubstep,
  getStepAndSubstepFromSlug,
  getStepTrackerConfig,
  patientInfoRoutes,
} from "./patientInformationNavigation";

export type UpdateAgeModalVariant = AgeModalScenario;

/**
 * Props for the NavButtons component
 */
interface NavButtonsProps {
  isMobile: boolean;
  currentStep: number;
  currentSubstep: number;
  onNavigate: (step: number, substep: number) => void;
  onBack: () => void;
  onUpdateAgeScenario?: (scenario: UpdateAgeModalVariant) => void;
}

const PatientInformationContent = styled("div", {
  width: "100%",
  maxWidth: LAYOUT_CONSTANTS.maxFormWidth,
  height: "100%",
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "start",
});

const StepContentWrapper = styled("div", {
  width: "100%",
  marginTop: "20px",
});

export const NavButtons = ({
  isMobile,
  currentStep,
  currentSubstep,
  onNavigate,
  onBack,
  onUpdateAgeScenario,
}: NavButtonsProps) => {
  const { handleSubmit, setHandleSubmit } = useFormContext();
  const { data } = useApplicationContext();
  const { componentName } = useComponentNameContext();
  const { isInSalesforce, publishToSalesforce } = useCanvasContext();
  const { runExclusive, isPending, isUiBlocked } = useExclusiveAction("patient-information:next");

  const handleBackClick = () => {
    // Log the click with a short delay to ensure it's sent before navigation
    // This is a special case that needs manual handling where we want to ensure logging before navigation
    ClientLogsService.logClick(
      data.userUuid || "unknown",
      componentName || "Patient Information",
      `patient-info-back-btn-${getFormNameForSubmit(currentStep, currentSubstep)}`,
      `Patient Information Back Button on ${getFormNameForSubmit(currentStep, currentSubstep)}`,
    );
    onBack();
  };

  /**
   * Validates patient age compatibility with selected provider for ALL age groups.
   * Returns true if validation passes (provider supports patient's age range),
   * returns false if validation fails (shows modal and prevents navigation).
   */
  const validateUnder18Age = (formDetails: FormDetails): boolean => {
    // Policy-holder details are for the insurance subscriber, not the patient — skip age check
    if (
      (currentStep === 1 && currentSubstep === 2) ||
      (currentStep === 3 && currentSubstep === 1)
    ) {
      return true;
    }

    const patientDob = formDetails.memberDOB;
    if (!patientDob) {
      // Cannot determine age — DOB missing, skipping age validation and allowing navigation.
      ClientLogsService.logWebAction(
        data.userUuid || "unknown",
        "NavButtons.validateUnder18Age",
        "DOB missing — skipping age validation and allowing navigation",
      );
      return true; // Missing DOB — treat as validation pass and allow through
    }

    const ageModalEvaluation = evaluateAgeModalScenario(patientDob, {
      selectedAgeRange: data.ageRange,
    });
    if (ageModalEvaluation.scenario === "age_range_mismatch") {
      ClientLogsService.logWebAction(
        data.userUuid || "unknown",
        "NavButtons.validateUnder18Age",
        "DOB does not match the selected age range. Showing age range mismatch modal.",
      );
      onUpdateAgeScenario?.("age_range_mismatch");
      return false;
    }

    return true;
  };

  /**
   * Handles navigation after form validation succeeds.
   * Routes to appropriate next step or substep based on current position.
   */
  const handleNavigation = (formDetails: FormDetails) => {
    if (currentStep === 1) {
      const shouldGoToSubstep2 = currentSubstep < 2 && formDetails.goToNextSubStep;
      onNavigate(shouldGoToSubstep2 ? 1 : 2, shouldGoToSubstep2 ? 2 : 1);
    } else if (currentStep === 3) {
      // Form submission in ConfirmApptDetails handles appointment scheduling
      setHandleSubmit(null);
    } else {
      onNavigate(currentStep + 1, 1);
    }
  };

  const handleNextClick = async () => {
    if (!handleSubmit) {
      return;
    }

    await runExclusive(async () => {
      const formDetails = await handleSubmit();

      if (!formDetails?.isCompleted) {
        setHandleSubmit(null);
        return;
      }

      if (!validateUnder18Age(formDetails)) {
        return; // Age validation failed, modal shown
      }

      // Publish Canvas outbound sync for Patient Navigator sessions.
      if (isInSalesforce && publishToSalesforce) {
        const slug = getSlugFromStepAndSubstep(currentStep, currentSubstep);
        const snapshot = formDetails.submittedData ?? data;

        if (currentStep === 3) {
          try {
            const appointmentDetails = snapshot.appointmentDetails;
            publishToSalesforce(
              CANVAS_OUTBOUND_EVENT_NAME,
              buildOutboundPayload(
                {
                  appointmentDetails: {
                    ...appointmentDetails,
                  },
                },
                `/patient-information/${slug}`,
                true,
              ),
            );
          } catch (error) {
            ClientLogsService.logError(
              data.userUuid || "unknown",
              "NavButtons.handleNextClick",
              "Canvas publish failed for step 3 (confirm-appointment)",
              error as Error,
            );
          }
        } else {
          publishToSalesforce(
            CANVAS_OUTBOUND_EVENT_NAME,
            buildOutboundPayload(snapshot, `/patient-information/${slug}`),
          );
        }
      }

      handleNavigation(formDetails);
      setHandleSubmit(null);
    });
  };

  return (
    <NavButtonsContainer
      isMobile={isMobile}
      data-testid={`test-id-patient-information-nav-buttons-step-${currentStep}-substep-${currentSubstep}`}
    >
      <Button
        icon={
          !isMobile ? { icon: "chevron_left", position: "leading", variant: "outlined" } : undefined
        }
        data-testid={`test-id-patient-information-back-button-step-${currentStep}-substep-${currentSubstep}`}
        id={`patient-info-back-btn-${getFormNameForSubmit(currentStep, currentSubstep)}`}
        data-obc-label={`Patient Information Back Button on ${getFormNameForSubmit(currentStep, currentSubstep)}`}
        onClick={handleBackClick}
        variant={!isMobile ? "outline" : "text"}
        size={isMobile ? "lg" : "md"}
      >
        Back
      </Button>
      <Button
        data-testid={`test-id-patient-information-next-button-step-${currentStep}-substep-${currentSubstep}`}
        id={`obc-patient-info-${currentStep === 3 ? "schedule-appointment" : "next"}-btn-${getFormNameForSubmit(currentStep, currentSubstep)}`}
        data-obc-label={`Patient Information ${currentStep === 3 ? "Schedule appointment" : "Next"} Button on ${getFormNameForSubmit(currentStep, currentSubstep)}`}
        type="submit"
        form={getFormNameForSubmit(currentStep, currentSubstep)}
        icon={
          currentStep !== 3 && !isMobile
            ? {
                icon: "chevron_right",
                position: "trailing",
                variant: "outlined",
              }
            : undefined
        }
        onClick={handleNextClick}
        size={isMobile ? "lg" : "md"}
        isDisabled={isPending || isUiBlocked}
      >
        {currentStep === 3 ? "Schedule appointment" : "Next"}
      </Button>
    </NavButtonsContainer>
  );
};

export const getIconName = (step: number): ValidIllustrationBrandName | string | null => {
  if (step === 1) {
    return "digital_insurance_card" as ValidIllustrationBrandName;
  } else if (step === 2) {
    return "interactive_counseling" as string;
  } else if (step === 3) {
    return "" as ValidIllustrationBrandName;
  } else {
    return null;
  }
};

export const getIconLabel = (step: number): string | undefined => {
  if (step === 1) {
    return "Digital insurance card image illustration";
  } else if (step === 2) {
    return "Member Interactive counseling image illustration";
  } else if (step === 3) {
    return "";
  } else {
    return undefined;
  }
};

export const renderStepContent = (step: number, substep: number) => {
  switch (step) {
    case 1:
      return substep === 1 ? <CoverCareStep /> : <PolicyHolderDetailsStep />;
    case 2:
      return <YourInfoStep />;
    case 3:
      return <ConfirmApptDetails />;
    default:
      return null;
  }
};

export const PatientInformation = () => {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigateWithUTM();
  const isMobile = useMediaQuery(MEDIA_QUERIES.mobileMax);
  const { data, setDataPatch } = useApplicationContext();
  const { step: currentStep, substep: currentSubstep } = getStepAndSubstepFromSlug(
    slug || patientInfoRoutes.COVER_CARE,
  );
  const [isUpdateAgeModalOpen, setIsUpdateAgeModalOpen] = useState(false);
  const [updateAgeModalVariant, setUpdateAgeModalVariant] =
    useState<UpdateAgeModalVariant>("age_range_mismatch");

  const infoStepLabel = useResolvedPageLabels("yourInfoStep").stepLabel;
  const { steps, currentStep: trackerCurrentStep } = getStepTrackerConfig(
    currentStep,
    infoStepLabel,
    data.paymentMethod,
  );

  const handleNavigate = (step: number, substep: number) => {
    navigate(getNavigationPath(step, substep));
  };

  const handleBack = () => {
    navigate(
      getBackNavigationPath(
        currentStep,
        currentSubstep,
        data.isPrimaryPolicyHolder,
        data.paymentMethod,
        data.providerBackPath,
      ),
    );
  };

  const handleUpdateAgeScenario = (scenario: UpdateAgeModalVariant) => {
    setUpdateAgeModalVariant(scenario);
    setIsUpdateAgeModalOpen(true);
  };

  useEffect(() => {
    const redirectPath = getSelfPayRedirectPath(slug, data.paymentMethod);
    if (redirectPath) {
      navigate(redirectPath, { replace: true });
    }
  }, [data.paymentMethod, navigate, slug]);

  return (
    <Fragment>
      <UpdateAgeModal
        data-testid="test-id-patient-information-update-age-modal"
        isOpen={isUpdateAgeModalOpen}
        text={
          <span>
            {"Date of birth does not match age range selected earlier ("}
            <b>{data.ageRange}</b>
            {"). Please update either age range or DOB."}
          </span>
        }
        variant={updateAgeModalVariant}
        onClose={() => {
          setIsUpdateAgeModalOpen(false);
        }}
        onConfirm={async () => {
          setIsUpdateAgeModalOpen(false);

          const shouldClearSessionType =
            data.isUnder12InPersonProceed &&
            (Array.isArray(data.sessionType)
              ? data.sessionType.includes("In-person")
              : data.sessionType === "In-person");

          setDataPatch({
            ageRange: "",
            dob: undefined,
            ...(shouldClearSessionType ? { sessionType: undefined } : {}),
            isUnder12InPersonProceed: false,
            yourInfoForm: undefined,
          });

          markAgeRangeResetConfirmation();
          navigate(APP_ROUTES.GET_STARTED);
        }}
      />
      {/* Set default meta tags title to cover care for Adobe Analytics */}
      <Router.MetaTags title="How would you like to cover care?" />
      <ToastProvider />
      <FormProvider>
        <PageLayout css={{ paddingBottom: isMobile ? "0" : "50px" }}>
          <IllustrationBG
            data-testid="test-id-patient-information-illustration-bg"
            iconName={getIconName(currentStep)}
            ariaLabel={getIconLabel(currentStep)}
          />
          <PatientInformationContent data-testid="test-id-patient-information-content">
            <StepTracker
              data-testid="test-id-patient-information-step-tracker"
              steps={steps}
              currentStep={trackerCurrentStep}
              hideStepNumbers
              hideStepStatus
              css={{
                "abyss-step-tracker-root": {
                  zIndex: 5,
                  width: "100%",
                },
                "abyss-step-tracker-mobile-step-label": {
                  display: "none",
                },
              }}
            />
            <StepContentWrapper data-testid="test-id-patient-information-step-content-wrapper">
              {renderStepContent(currentStep, currentSubstep)}
            </StepContentWrapper>

            {FormFooter(currentStep)}
            <NavButtons
              isMobile={isMobile}
              currentStep={currentStep}
              currentSubstep={currentSubstep}
              onNavigate={handleNavigate}
              onBack={handleBack}
              onUpdateAgeScenario={handleUpdateAgeScenario}
            />
          </PatientInformationContent>
        </PageLayout>
      </FormProvider>
    </Fragment>
  );
};
